import React from "react";
import type { TopologyDevice, TopologyHub } from "../lib/topologyAdapter";
import HealthPill from "./HealthPill";

const ACCENT_COLOR: Record<TopologyHub["health"], string> = {
  good: "var(--color-success)",
  warning: "var(--color-warning)",
  critical: "var(--color-danger)",
  unknown: "var(--color-neutral)",
};

export interface HubHealthGridProps {
  hubs: TopologyHub[];
  devices: TopologyDevice[];
}

/**
 * Hub health cards: name, health pill, uplink, device count. Deliberately
 * NO power row — this hardware has no live power telemetry (see
 * src/api/hub-health.ts), so a power meter here would be fabricated data.
 */
export default function HubHealthGrid({ hubs, devices }: HubHealthGridProps) {
  if (hubs.length === 0) {
    return (
      <p style={{ color: "var(--color-text-secondary)", fontSize: "var(--font-size-sm)" }}>
        No USB hubs reported for this host yet.
      </p>
    );
  }

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
        gap: "var(--space-md)",
      }}
    >
      {hubs.map((hub) => {
        const hubDevices = devices.filter((d) => d.hubId === hub.id);
        const offlineCount = hubDevices.filter((d) => d.isOffline).length;
        const degradedCount = hubDevices.filter((d) => d.isDegraded).length;

        return (
          <div
            key={hub.id}
            style={{
              background: "var(--color-surface)",
              borderRadius: "var(--radius-md)",
              boxShadow: "var(--shadow-card)",
              padding: "var(--space-md)",
              borderLeft: `4px solid ${ACCENT_COLOR[hub.health]}`,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "var(--space-sm)",
              }}
            >
              <div>
                <h3 style={{ margin: 0, fontSize: "var(--font-size-md)", color: "var(--color-primary)" }}>
                  {hub.name}
                </h3>
                <div style={{ fontSize: "var(--font-size-xs)", color: "var(--color-text-secondary)" }}>
                  {hub.locationId}
                </div>
              </div>
              <HealthPill status={hub.health} />
            </div>
            <Row label="Uplink" value={hub.uplinkHubId ? "Via another hub" : "Host controller"} />
            <Row label="Devices" value={String(hub.deviceCount)} />
            <div
              style={{
                fontSize: "var(--font-size-xs)",
                color: "var(--color-text-secondary)",
                marginTop: "var(--space-xs)",
              }}
            >
              {statusNote(hub, offlineCount, degradedCount)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        fontSize: "var(--font-size-sm)",
        margin: "5px 0",
      }}
    >
      <span style={{ color: "var(--color-text-secondary)" }}>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function statusNote(hub: TopologyHub, offlineCount: number, degradedCount: number): string {
  if (hub.health === "unknown") {
    return "Last USB scan is stale — no recent report from this hub.";
  }
  if (offlineCount > 0 && degradedCount > 0) {
    return `${offlineCount} device(s) offline · ${degradedCount} on a degraded link.`;
  }
  if (offlineCount > 0) return `${offlineCount} device(s) offline.`;
  if (degradedCount > 0) return `${degradedCount} device(s) on a degraded link.`;
  return "All links healthy.";
}
